// A public link's page (/s/<link>): the board as its owner shares it, into the store the app draws from. The page's
// own socket (src/public/PublicBoard.tsx) keeps it live; reading it again is here, so a page left open takes what
// changed while it was away, and never loads the viewer's own board, whoever they are signed in as.

import type { Activity, AgentState, Board, CustomTheme, Entry, Item, Stream } from "../../shared/types";
import { SHARE_PATH } from "../../shared/sharePage";
import { api } from "../lib/api";
import { setMadeThemes } from "../themes/themes";
import { set } from "./store";

export interface PublicSnap {
  board: Board;
  owner: { name: string; handle: string | null; xPremium?: boolean };
  items: Item[];
  /** a typed board's own pages, without who changed them */
  entries?: Entry[];
  streams: Stream[];
  agents: AgentState[];
  activity: Activity[];
  serverTime: number;
  /** the made theme the board wears, when it wears one, without who made it or the links its pictures came from */
  theme?: CustomTheme | null;
}

/** The link this page shows, or null when it is not a public link's page. */
export const publicSlugHere = (): string | null => (typeof location === "undefined" ? null : (SHARE_PATH.exec(location.pathname)?.[1] ?? null));

export function loadPublic(snap: PublicSnap) {
  const items: Record<string, Item> = {};
  for (const i of snap.items) items[i.id] = i;
  const entries: Record<string, Entry> = {};
  for (const e of snap.entries ?? []) entries[e.id] = e;
  const streams: Record<string, Stream> = {};
  for (const s of snap.streams) streams[s.key] = s;
  const agents: Record<string, AgentState> = {};
  for (const a of snap.agents) agents[a.name] = a;
  if (snap.theme) setMadeThemes([snap.theme]);
  set({ boards: [snap.board], boardId: snap.board.id, items, entries, streams, agents, activity: snap.activity, themes: snap.theme ? { [snap.theme.id]: snap.theme } : {}, serverOffset: snap.serverTime - Date.now(), ownerXPremium: snap.owner.xPremium === true, phase: "ready", keys: [] });
}

/** The board read again, past the ten seconds a browser may keep the last answer. A link that cannot be read leaves the page as it is. */
export async function reloadPublic(slug = publicSlugHere()): Promise<PublicSnap | null> {
  if (!slug) return null;
  try {
    const snap = await api<PublicSnap>(`/api/public/${slug}?fresh=${Date.now()}`, { token: null });
    loadPublic(snap);
    return snap;
  } catch {
    return null;
  }
}
